import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { CurrencyCode } from "@domain/currency/currency";
import { Language } from "@domain/currentProfile/currentProfile";
import { NotificationCategory, NotificationMode } from "@domain/notification/notification";
import { useAsync, useChangeEffect, useIsMobile } from "@utils/hooks";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ArrowIcon, ChevronDownIcon } from "@ui/components/icons";
import { Row, row, Stack } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { fromLeft, fromRight } from "@ui/styles/keyframes";
import { gap, mobile } from "@ui/styles/utils";
import { LoginSettings } from "./loginSettings";
import { MobileNotificationSettings } from "./mobileNotificationSettings";
import { More } from "./more";
import { NotificationSettings } from "./notificationSettings";
import { SystemSettings } from "./systemSettings";

const tabs = ["SYSTEM", "LOGIN", "NOTIFICATIONS", "MORE"] as const;

export const SettingsPage: React.FC = () => {
	const isMobile = useIsMobile();
	const [tabIndex, setTabIndex] = useState<number | null>(isMobile ? null : 0);
	const currentTab = tabIndex !== null ? tabs[tabIndex] : undefined;
	const [hasLoaded, setHasLoaded] = useState(false);
	const [currentYear, setCurrentYear] = useState<Date>(new Date());

	const { currentProfileService, notificationService } = useServices();

	const { loading: notificationsLoading, run: setNotificationsSettings } = useAsync(
		(item: NotificationCategory, mode: NotificationMode, enabled: boolean) =>
			notificationService.setEnabledSettings({ category: item, mode }, enabled),
		[]
	);

	const { run: setSystemSettings, loading: profileLoading } = useAsync(
		(language: Language) =>
			currentProfileService.saveProfileInfos({
				language,
			}),
		[]
	);
	const { run: setCurrencySettings, loading: currencyProfileLoading } = useAsync(
		(currency: CurrencyCode) =>
			currentProfileService.saveProfileInfos({
				currency,
			}),
		[]
	);

	useChangeEffect(() => {
		setHasLoaded(true);
	}, [notificationsLoading, profileLoading, currencyProfileLoading]);

	useEffect(() => {
		setCurrentYear(new Date());
		if (!currentTab && !isMobile) {
			setTabIndex(0);
		}
	}, [currentTab]);

	return (
		<Container>
			{!isMobile || currentTab === undefined ? (
				<LeftMenuWrapper>
					{isMobile && <Title>{I18n.t("settings.menu.title")}</Title>}
					<LeftMenu>
						{!isMobile && <Title>{I18n.t("settings.menu.title")}</Title>}
						<Stack gap={20}>
							{tabs.map((tab, index) => (
								<TabView key={tab} onClick={() => setTabIndex(index)}>
									<Label selected={tabIndex === index}>
										<b>{I18n.t(`settings.menu.${tab.toLowerCase()}`)}</b>
									</Label>
									<ChevronDownIcon />
								</TabView>
							))}
						</Stack>
					</LeftMenu>
					<Copyright>
						<b>{I18n.t("more.copyright", { currentYear: currentYear.getFullYear() })}</b>
					</Copyright>
				</LeftMenuWrapper>
			) : null}
			{isMobile && currentTab ? (
				<MobileRightSide>
					<Row gap={20} align="center" onClick={() => setTabIndex(null)}>
						<ArrowIcon />
						<Title>
							<div>
								{currentTab === "NOTIFICATIONS"
									? I18n.t("settings.notifications.title")
									: currentTab === "SYSTEM"
										? I18n.t("settings.system.title")
										: currentTab === "LOGIN"
											? I18n.t("settings.login.title")
											: I18n.t("settings.more.title")}
							</div>
						</Title>
					</Row>
					{currentTab === "NOTIFICATIONS" ? (
						<>
							{hasLoaded &&
								(notificationsLoading ? (
									<ClipLoader size={16} />
								) : (
									<Success>{I18n.t("settings.save.success")}</Success>
								))}
							<MobileNotificationSettings onSettingsChange={setNotificationsSettings} />
						</>
					) : currentTab === "SYSTEM" ? (
						<>
							{hasLoaded &&
								(profileLoading || currencyProfileLoading ? (
									<ClipLoader size={16} />
								) : (
									<Success>{I18n.t("settings.save.success")}</Success>
								))}
							<ContentBlock>
								<SystemSettings onLanguageChange={setSystemSettings} onCurrencyChange={setCurrencySettings} />
							</ContentBlock>
						</>
					) : currentTab === "LOGIN" ? (
						<>
							{hasLoaded &&
								(profileLoading || currencyProfileLoading ? (
									<ClipLoader size={16} />
								) : (
									<Success>{I18n.t("settings.save.success")}</Success>
								))}
							<ContentBlock>
								<LoginSettings />
							</ContentBlock>
						</>
					) : (
						<More />
					)}
				</MobileRightSide>
			) : currentTab === "NOTIFICATIONS" ? (
				<ContentBlock>
					<Title>
						<div>{I18n.t("settings.notifications.title")}</div>
						{hasLoaded &&
							(notificationsLoading ? <ClipLoader size={16} /> : <Success>{I18n.t("settings.save.success")}</Success>)}
					</Title>
					<NotificationSettings onSettingsChange={setNotificationsSettings} />
				</ContentBlock>
			) : currentTab === "SYSTEM" ? (
				<ContentBlock>
					<Title>
						<div>{I18n.t("settings.system.title")}</div>
						{hasLoaded &&
							(profileLoading || currencyProfileLoading ? (
								<ClipLoader size={16} />
							) : (
								<Success>{I18n.t("settings.save.success")}</Success>
							))}
					</Title>
					<SystemSettings onLanguageChange={setSystemSettings} onCurrencyChange={setCurrencySettings} />
				</ContentBlock>
			) : currentTab === "LOGIN" ? (
				<ContentBlock>
					<Title>
						<div>{I18n.t("settings.login.title")}</div>
						{hasLoaded &&
							(profileLoading || currencyProfileLoading ? (
								<ClipLoader size={16} />
							) : (
								<Success>{I18n.t("settings.save.success")}</Success>
							))}
					</Title>
					<LoginSettings />
				</ContentBlock>
			) : currentTab === "MORE" ? (
				<More />
			) : null}
		</Container>
	);
};

const Container = styled(Page)`
	width: 100%;
	margin-bottom: 40px;
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 45px;
	grid-row-gap: 24px;
	grid-template-areas:
		"menu content"
		". content"
		". content"
		". content";

	@media ${mobile} {
		display: block;
	}
`;

const LeftMenuWrapper = styled.div`
	grid-area: menu;
	position: sticky;
	${gap(20)};
	@media ${mobile} {
		${gap(30)};
		animation: ${fromLeft} 0.2s linear;
	}
`;

const LeftMenu = styled(RoundedBlock)`
	padding: 40px 31px 40px 36px;
	${gap(38)};
`;

const Title = styled.div`
	${row(16, "flex-start", "baseline")};
	${({ theme }) => theme.textStyles.subtitle};
`;

const TabView = styled.div`
	cursor: pointer;
	width: 100%;
	${row(0, "space-between", "center")};
	padding: 5px 0px;
	will-change: transform;
	svg  {
		transition: transform ease-in-out 0.2s;
		transform: rotate(-90deg);
	}
	&:hover svg {
		transform: rotate(-90deg) translateY(2px);
	}
`;

const Label = styled(Primary)<{ selected?: boolean }>`
	color: ${({ theme, selected }) => (selected ? theme.colors.secondary : theme.colors.textPrimary)};
`;

const MobileRightSide = styled.div`
	${gap(30)};
	animation: ${fromRight} 0.2s linear;
`;

const ContentBlock = styled(RoundedBlock)`
	grid-area: content;
	padding: 60px 57px;
	${gap(60)};

	@media ${mobile} {
		padding: 22px;
	}
`;

const Copyright = styled.div`
	${({ theme }) => theme.textStyles.lighter};
`;

const Success = styled.div`
	color: ${({ theme }) => theme.colors.green};
	font-size: 0.8125rem;
`;
